import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Button, 
  Row, 
  Col, 
  Spinner, 
  Alert 
} from 'react-bootstrap';
import PracticeQuestionSession from './PracticeQuestionSession';
import { apiService } from '../../../services/apiService';

const PracticeMode = ({ onSessionStart, onSessionEnd }) => {
  const [jobDescription, setJobDescription] = useState(null);
  const [resume, setResume] = useState(null);
  const [questionCount, setQuestionCount] = useState(7);
  const [questionType, setQuestionType] = useState('technical');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);

  const handleJobDescriptionChange = (e) => {
    setJobDescription(e.target.files[0]);
  };

  const handleResumeChange = (e) => {
    setResume(e.target.files[0]);
  };

  const handleQuestionCountChange = (e) => {
    setQuestionCount(parseInt(e.target.value));
  };

  const handleQuestionTypeSelect = (type) => {
    setQuestionType(type);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!jobDescription) {
      setError('Please upload a job description file');
      return;
    }

    if (!resume) {
      setError('Please upload your resume file');
      return;
    }

    setLoading(true);
    setError('');
    setQuestions(null);
    setSessionActive(false);

    const formData = new FormData();
    formData.append('job_description', jobDescription);
    formData.append('resume', resume);
    formData.append('question_count', questionCount);
    formData.append('question_type', questionType);

    try {
      const totalSizeMB = (jobDescription.size + resume.size) / (1024 * 1024);
      if (totalSizeMB > 5) {
        setError(`Processing ${Math.round(totalSizeMB)}MB of material, this may take several minutes. Please wait...`);
      }

      const data = await apiService.generatePracticeQuestions(formData);
      if (data && data.questions) {
        setQuestions(data.questions);
        setSessionActive(true);
        onSessionStart();
        setError('');
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Error generating interview questions');
    } finally {
      setLoading(false);
    }
  };

  const handleEndSession = () => {
    setSessionActive(false);
    onSessionEnd();
  };

  if (sessionActive && questions) {
    return (
      <PracticeQuestionSession 
        questions={questions} 
        onEndSession={handleEndSession}
      />
    );
  }

  return (
    <Card>
      <Card.Body>
        <Card.Title className="mb-4">Generate Interview Practice Questions</Card.Title>

        {error && <Alert variant={error.includes('Processing') ? "info" : "danger"}>{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="jobDescription">
                <Form.Label>Upload Job Description</Form.Label>
                <Form.Control 
                  type="file" 
                  onChange={handleJobDescriptionChange} 
                  accept=".pdf,.docx,.doc,.txt"
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="resume">
                <Form.Label>Upload Your Resume</Form.Label>
                <Form.Control 
                  type="file" 
                  onChange={handleResumeChange} 
                  accept=".pdf,.docx,.doc,.txt"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="questionType">
                <Form.Label>Question Type</Form.Label>
                <Form.Control 
                  as="select"
                  value={questionType}
                  onChange={(e) => handleQuestionTypeSelect(e.target.value)}
                >
                  <option value="behavioral">Behavioral</option>
                  <option value="technical">Technical</option>
                  <option value="situational">Situational</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-grid gap-2">
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading || !jobDescription || !resume}
              size="lg"
            >
              {loading ? <><Spinner animation="border" size="sm" /> Generating Questions...</> : 'Generate Interview Questions'}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default PracticeMode;
